import React from 'react';
import classNames from 'classnames';
import { Link as ReactRouterLink } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { Weights } from '../title';
import { colors, spacing } from '../../style';
import { sizes, variants, decorations } from './style';

export interface UiLinkProps {
  weight?: Weights;
  capitalize?: boolean;
  style?: React.CSSProperties;
  to?: string | (() => void);
  disabled?: boolean;
  clean?: boolean;
  className?: string;
  size?: keyof typeof sizes;
  componentClass?: React.ElementType;
  href?: string;
  variant?: keyof typeof variants;
  decoration?: keyof typeof decorations;
  active?: boolean;
  block?: boolean;
  display?: 'flex' | 'block' | 'inline-block';
  'aria-label'?: string;
  'data-testid'?: string;
  alignItems?: 'center' | 'flex-start' | 'flex-end';
  flexGrow?: number;
  children?: React.ReactNode;
  textTransform?: 'capitalize';
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  onMouseEnter?: (e: React.MouseEvent<HTMLElement>) => void;
  onMouseLeave?: (e: React.MouseEvent<HTMLElement>) => void;
  activeClassName?: string;
  target?: HTMLAnchorElement['target'];
  ref?: React.Ref<any>;
  align?: 'left' | 'right' | 'center';
  gap?: keyof typeof spacing;
  offset?: number;
  smooth?: boolean;
  containerId?: string;

  wordBreak?: React.CSSProperties['wordBreak'];
  wordWrap?: 'break-word';
}

/********
 UILink

 is a component that only gives styling to the React Router link components.
 It also knows to render the correct componentClass\TagName according to props that are passed.
 If an href is passed we need to render an anchor, if "to" prop is passed we will use the React Router link.
 if none are passed, we will use the "button" component for accessibility reasons.

 *******/
const UiLinkComponent = ({
  className,
  componentClass: TagName,
  disabled,
  to,
  href,
  active,
  block,
  'aria-label': ariaLabel,
  size = 'lg',
  ...rest
}: UiLinkProps) => {
  let Component;

  if (href) {
    Component = 'a';
  } else if (to) {
    Component = TagName || ReactRouterLink;
  } else {
    Component = 'button';
  }

  return (
    <Component
      to={disabled ? '#' : to}
      href={disabled ? null : href}
      type={Component === 'button' ? 'button' : undefined}
      data-bi
      disabled={disabled}
      className={classNames('box-link', className, { active }, { block })}
      aria-label={ariaLabel}
      size={size}
      {...rest}
    />
  );
};

const UiLink = styled(UiLinkComponent).withConfig({
  shouldForwardProp: prop =>
    !['activeClassName', 'alignItems', 'display', 'flexGrow', 'variant', 'size', 'weight', 'decoration'].includes(prop),
})<UiLinkProps>`
  ${props => props.variant && variants[props.variant]};
  ${props => props.size && sizes[props.size]};
  text-decoration: ${props => decorations[props.decoration || 'none']};
  font-weight: ${props => props.weight};
  text-decoration: none;
  text-transform: ${props => props.textTransform};
  display: ${props => props.display};
  align-items: ${props => props.alignItems};
  cursor: pointer;
  text-align: ${props => props.align};
  gap: ${props => props.gap && spacing[props.gap]};
  word-break: ${props => props.wordBreak};

  ${props =>
    props.flexGrow &&
    css`
      flex-grow: ${props.flexGrow};
    `};

  &[disabled] {
    cursor: not-allowed;
  }

  &:focus-visible:not(.disabled):not([disabled]) {
    border: 1px solid ${colors.sky};
  }

  &[type='button'] {
    background: transparent;
    border: 1px solid transparent;
  }
`;

UiLink.displayName = 'UiLink';
export default UiLink;
